// src/features/learner/components/ContentRenderers/FlipCardRenderer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../../units/FlipCardViewer.css';  // Shared CSS from units directory
import globalAudioManager from '../../../MediaLibrary/GlobalAudioManager';

const FlipCardRenderer = ({ 
  unit, 
  completedUnits = {},
  onComplete,
  onResetUnit
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [hasFlipped, setHasFlipped] = useState(false);
  const [frontViewed] = useState(true); // Front is viewed on load
  const [backViewed, setBackViewed] = useState(false);
  const [showFlipHint, setShowFlipHint] = useState(true);
  const [enableTransition, setEnableTransition] = useState(false); // Control animation
  // Track which self-assessment button was pressed ('learnt', 'dunno', or null)
  const [selectedStatus, setSelectedStatus] = useState(null);
  const audioRef = useRef(null);
  const cardRef = useRef(null);

  // Parse content
  const [content, setContent] = useState({
    front: { text: '', image: '', audio: '' },
    back: { text: '', image: '', audio: '' }
  });

  useEffect(() => {
    if (!unit?.content) return;

    // Disable transition for instant card switch (no animation)
    setEnableTransition(false);
    
    // Reset flip state when card changes
    setIsFlipped(false);
    setHasFlipped(false);
    setBackViewed(false);
    setShowFlipHint(true);
    // Restore previously saved status so the dot colour persists across navigation
    const saved = completedUnits[unit?.id];
    setSelectedStatus(saved === 'learnt' || saved === 'dunno' ? saved : null);

    try {
      const parsed = typeof unit.content === 'string' ? JSON.parse(unit.content) : unit.content;
      if (parsed.front || parsed.back) {
        setContent({
          front: {
            text: parsed.front?.text || '',
            image: parsed.front?.image || '',
            audio: parsed.front?.audio || ''
          },
          back: {
            text: parsed.back?.text || '',
            image: parsed.back?.image || '',
            audio: parsed.back?.audio || ''
          }
        });
        
        // Re-enable transition after card is loaded (for user flips)
        setTimeout(() => setEnableTransition(true), 50);
        
        // Auto-play front audio on load
        if (parsed.front?.audio) {
          setTimeout(() => playAudio(parsed.front.audio), 500);
        }
      }
    } catch (error) {
      console.error('Error parsing flipcard content:', error);
    }
  }, [unit]);

  // Play audio function
  const playAudio = useCallback((audioUrl) => {
    if (!audioUrl) return;

    // Stop any current audio using global manager
    globalAudioManager.stopAll();

    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    
    audio.play().catch(err => {
      console.error('Audio play failed:', err);
    });
  }, []);

  // Handle flip
  const handleFlip = useCallback(() => {
    const newFlippedState = !isFlipped;
    setIsFlipped(newFlippedState);
    setHasFlipped(true);
    setShowFlipHint(false);

    // Track which side was viewed
    if (newFlippedState) {
      setBackViewed(true);
      // Play back audio
      if (content.back.audio) {
        setTimeout(() => playAudio(content.back.audio), 300); // Delay for flip animation
      }
    } else {
      // Play front audio
      if (content.front.audio) {
        setTimeout(() => playAudio(content.front.audio), 300);
      }
    }
  }, [isFlipped, content, playAudio]);

  // Touch handlers for mobile
  const handleTouchStart = useCallback((e) => {
    // Prevent double-tap zoom
    e.preventDefault();
  }, []);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFlip();
  }, [handleFlip]);

  // Keyboard support
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        handleFlip();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleFlip]);

  // Render card side content
  const renderSideContent = (sideData, isBack = false) => {
    const hasImage = !!sideData.image;
    const hasText = !!sideData.text;
    
    return (
      <div 
        className="card-content"
        style={{
          flex: 1,
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {hasImage && (
          <div 
            className="card-image-container"
            style={{
              marginBottom: hasText ? '15px' : '0',
              flex: hasText ? '0 0 auto' : '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: hasText ? 'auto' : '300px'
            }}
          >
            <img 
              src={sideData.image} 
              alt={isBack ? "Back side" : "Front side"} 
              className="card-image"
              style={{
                maxWidth: '100%',
                maxHeight: hasText ? '250px' : '350px',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '6px'
              }}
              loading="lazy"
            />
          </div>
        )}
        
        {hasText && (
          <div 
            className="card-text"
            style={{
              flex: hasImage ? '0 0 auto' : '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              width: '100%'
            }}
          >
            <div 
              className="ql-editor"
              dangerouslySetInnerHTML={{ 
                __html: sideData.text 
              }}
            />
          </div>
        )}
        
        {/* Show placeholder only when both image and text are empty */}
        {!hasImage && !hasText && (
          <div 
            className="card-text placeholder-text"
            style={{
              flex: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: '#999',
              fontStyle: 'italic'
            }}
          >
            {isBack ? 'Back side content...' : 'Front side content...'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flipcard-viewer">
      <div className="card-wrapper">
        <div 
          ref={cardRef}
          className={`flipcard ${isFlipped ? 'flipped' : ''} ${hasFlipped ? 'has-flipped' : ''} ${!enableTransition ? 'no-transition' : ''}`}
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          tabIndex={0}
          role="button"
          aria-label={`Flipcard: ${unit?.title}. ${isFlipped ? 'Back side' : 'Front side'}. Tap to flip.`}
        >
          {/* Front Side */}
          <div className="card-side card-front">
            {renderSideContent(content.front, false)}
            
            {showFlipHint && (
              <div className="flip-hint">
                <div className="flip-icon">🔄</div>
                <div className="flip-text">Tap to flip</div>
              </div>
            )}
          </div>
          
          {/* Back Side */}
          <div className="card-side card-back">
            {renderSideContent(content.back, true)}
          </div>
        </div>
        
        {/* Learning Actions - Only show after both sides viewed */}
        {frontViewed && backViewed && (
          <div className="learning-actions">
            {selectedStatus === null ? (
              // No choice made yet — show both buttons
              <div className="action-buttons">
                <button 
                  className="action-btn learnt-btn"
                  onClick={() => {
                    setSelectedStatus('learnt');
                    onComplete && onComplete(unit.id, 'learnt');
                  }}
                >
                  ✅ I Know This
                </button>
                <button 
                  className="action-btn dunno-btn"
                  onClick={() => {
                    setSelectedStatus('dunno');
                    onComplete && onComplete(unit.id, 'dunno');
                  }}
                >
                  🤔 Still Learning
                </button>
              </div>
            ) : (
              // Choice made — show status message and redo button
              selectedStatus === 'dunno' ? (
                <div className="action-buttons">
                  <div style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    backgroundColor: '#e8a327',
                    color: '#fff',
                    marginBottom: '10px',
                    textAlign: 'center'
                  }}>
                    🤔 Still Learning - Review Again
                  </div>
                  <button
                    className="action-btn"
                    onClick={() => {
                      setSelectedStatus(null);
                      setIsFlipped(false);
                      setHasFlipped(false);
                      setBackViewed(false);
                      setShowFlipHint(true);
                      if (onResetUnit) onResetUnit(unit.id);
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      fontSize: '18px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto'
                    }}
                    title="Try again"
                  >
                    🔄
                  </button>
                </div>
              ) : (
                // selectedStatus === 'learnt' — just show confirmation, no retry
                <div style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  backgroundColor: '#19a4a0',
                  color: '#fff',
                  textAlign: 'center'
                }}>
                  ✅ I Know This
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlipCardRenderer;